import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday' // For setting weekday locale
import moment from 'moment'
import { Culture, DateLocalizer, Formats } from 'react-big-calendar'

dayjs.extend(isBetween)
dayjs.extend(weekday) // To get weekday info

const dayNameToIndex: { [key: string]: number } = {
  Friday: 5,
  Monday: 1,
  Saturday: 6,
  Sunday: 0,
  Thursday: 4,
  Tuesday: 2,
  Wednesday: 3
}

function getDayIndex(dayName: string): number | undefined {
  return dayNameToIndex[dayName]
}

export const getMonthsInRange = (startDate: string, endDate: string) => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  const months = []
  let current = start.startOf('month')

  while (current.isBefore(end) || current.isSame(end, 'month')) {
    months.push(current.format('MMMM YYYY')) // Push month name (e.g., January 2024)
    current = current.add(1, 'month')
  }

  return months
}

// Function to get all dates that match the specified day of the week in the range
export const getDatesForDayInRange = (
  startDate: string,
  endDate: string,
  dayName: any,
  month?: string // Optional month filter (e.g., "October 2024")
): string[] => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  const targetDay = getDayIndex(dayName) // Get day index from day name (Sunday = 0, Monday = 1, etc.)

  const dates: string[] = []
  let current = start

  while (current.isBefore(end) || current.isSame(end)) {
    if (current.day() === targetDay) {
      if (current.format('MMMM YYYY') === month) {
        dates.push(current.format('YYYY-MM-DD')) // Push date string
      }
    }
    current = current.add(1, 'day')
  }

  return dates
}

export const timeFormatter = (arg: string) => {
  const time = dayjs(arg).subtract(1, 'h').format('h:mma')
  return time
}

export const scheduleFormatter = (arg: Date | string, format?: string) => {
  const time = dayjs(arg).format(format || 'h:mma')
  return time
}

export const daysInMonth: any = (month: string) => new Date(new Date().getFullYear(), +month, 0).getDate()

export const formats: Formats = {
  dayFormat: (date: Date, culture?: Culture, localizer?: DateLocalizer): string =>
    localizer ? localizer.format(date, 'ddd DD', culture) : '',

  timeGutterFormat: (date: Date, culture?: Culture, localizer?: DateLocalizer): string =>
    localizer ? localizer.format(date, 'ha', culture) : ''
}

export const dateRangeFormat = (startDate: string, endDate: string) => {
  dayjs.extend(localeData)
  dayjs.extend(weekday)
  dayjs.locale('en')

  if (startDate && endDate) {
    const start = dayjs(startDate)
    const end = dayjs(endDate)

    // Check if start date is after end date

    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

    // Create an array to store the days of the week
    const range = []

    // Check if the range is longer than a week
    if (end.diff(start, 'day') >= 6) {
      // If more than a week, return all days from Monday to Sunday
      return daysOfWeek
    } else {
      // Otherwise, only return days within the range, from the `startDate` to `endDate`
      const startDayIndex = start.day() === 0 ? 6 : start.day() - 1 // Adjust for Sunday
      const endDayIndex = end.day() === 0 ? 6 : end.day() - 1

      if (startDayIndex > endDayIndex) {
        const totalIndex = startDayIndex + end.diff(start, 'day')
        for (let i = startDayIndex; i <= totalIndex; i++) {
          range.push(daysOfWeek[i % 7])
        }
      } else {
        for (let i = startDayIndex; i <= endDayIndex; i++) {
          range.push(daysOfWeek[i % 7])
        }
      }
    }

    return range
  } else {
    return []
  }
}

export const formattedStartTime = (date: string | Date, time: Date) => {
  const mergedDate = new Date(date)

  // Get hours, minutes, and seconds from the startTime and apply to the mergedDate
  mergedDate.setHours(time.getHours())
  mergedDate.setMinutes(time.getMinutes())
  mergedDate.setSeconds(time.getSeconds())
  mergedDate.setMilliseconds(0) // Set milliseconds to 0 for consistency

  return mergedDate
}

export function getSpecificDaysBetweenDates(startDate: string, endDate: string, dayOfWeek: string): string[] {
  const start = moment(startDate)
  const end = moment(endDate)

  const result: string[] = []

  // Find the first occurrence of the specified day of the week
  const current = start.clone().day(dayOfWeek)
  if (current.isBefore(start)) {
    current.add(7, 'days')
  }

  // Loop through and add all the specified days until reaching the end date
  while (current.isSameOrBefore(end)) {
    result.push(current.format('YYYY-MM-DD'))
    current.add(7, 'days')
  }

  return result
}

export function combineDateAndTime(dateWithDate: string, dateWithTime: string): string {
  const datePart = moment.utc(dateWithDate).format('YYYY-MM-DD')

  const timePart = moment.utc(dateWithTime).format('HH:mm:ss')

  const combinedDateTime = moment.utc(`${datePart}T${timePart}`).toISOString()

  return combinedDateTime
}

export function getOneHourBefore(dateString: string) {
  return moment(dateString).subtract(1, 'hours')
}

export function getOneHourAfter(dateString: string) {
  return moment(dateString).add(1, 'hours')
}

export function isAllowed(dateString: string) {
  const now = Date.now()
  const date = new Date(dateString).getTime()
  const oneHour = 2 * 60 * 60 * 1000
  return now >= date - oneHour && now <= date + oneHour
}

export function formatTo12Hour(timeString: string): string {
  return moment.utc(timeString).format('HH:mm')
}

export const createCustomTimeMask = (baseTime: string) => {
  const startHour = getOneHourBefore(baseTime).hour()
  const endHour = getOneHourAfter(baseTime).hour()
  const hourOptions =
    startHour > endHour
      ? [
          ...Array.from({ length: 24 - startHour }, (_, k) => startHour + k),
          ...Array.from({ length: endHour + 1 }, (_, k) => k)
        ]
      : Array.from({ length: endHour - startHour + 1 }, (_, k) => startHour + k)

  return [
    {
      length: [1, 2],
      options: hourOptions,
      placeholder: 'hh',
      regexp: /^(0?[0-9]|1[0-9]|2[0-3])$/
    },
    { fixed: ':' },
    {
      length: 2,
      options: ['00', '15', '30', '45'],
      placeholder: 'mm',
      regexp: /^[0-5][0-9]$/
    }
  ]
}

export function appendTimeToToday(dateString: string) {
  const inputDate = dateString?.includes('T') ? parseIsoToLocalDate(dateString) : new Date(dateString)
  const now = new Date()

  // Get today's date parts
  const year = now.getFullYear()
  const month = now.getMonth()
  const day = now.getDate()

  // Get time parts from input (use local time to avoid timezone shift)
  const hours = inputDate.getHours()
  const minutes = inputDate.getMinutes()
  const seconds = inputDate.getSeconds()
  const ms = inputDate.getMilliseconds()

  // Create new Date with today's date and input's time
  return new Date(year, month, day, hours, minutes, seconds, ms)
}

/**
 * Parse an ISO date string so the displayed time matches the API time (avoids timezone +1 hour shift).
 * Use for start_time/end_time from the API when they are in UTC.
 */
export function parseIsoToLocalDate(isoString: string): Date {
  if (!isoString || typeof isoString !== 'string') return new Date(isoString || 0)
  const d = new Date(isoString)
  if (Number.isNaN(d.getTime())) return new Date(isoString)
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
    d.getUTCMilliseconds()
  )
}

/**
 * Length of a medication order in whole days (end = start + N × 24h), or null when
 * either date is missing/invalid. A 5-day course started Monday 10:00 ends Saturday 10:00.
 */
export function medicationDurationDays(start: unknown, end: unknown): number | null {
  if (!start || !end) return null
  const startMs = new Date(start as string | Date).getTime()
  const endMs = new Date(end as string | Date).getTime()
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000))
}
